/**
 * Carga PEREZOSA del bundle de guías (`veo-guides.js`). El `veo.js` de
 * producción no incluye los renderers ni Floating UI; este loader inyecta el
 * bundle de guías y resuelve con su API (`window.veoGuides`) la primera vez que
 * hace falta (`guides:true` o `veo.preview()`).
 *
 * No importa NADA del plugin guides como valor (solo tipos, que se borran) para
 * no arrastrar su código al bundle principal — igual que `builder-loader`.
 */
import type {
  GuidesPluginConfig,
  PreviewGuideInput,
  PreviewHandle,
  PreviewModeHandle,
  PreviewModeOptions,
} from '../plugins/guides';
import type { VeoPlugin } from '../types';
import { hasDocument, hasWindow } from '../utils/safe-env';

export interface VeoGuidesApi {
  guidesPlugin: (config: GuidesPluginConfig) => VeoPlugin;
  previewGuide: (input: PreviewGuideInput) => PreviewHandle;
  closeGuidePreview: () => void;
  runPreviewMode: (opts: PreviewModeOptions) => Promise<PreviewModeHandle | null>;
}

interface WindowWithGuides {
  veoGuides?: VeoGuidesApi;
}

let cached: Promise<VeoGuidesApi | null> | null = null;

/** Carga (una vez) el bundle de guías y resuelve con su API, o `null` si falla. */
export function loadGuides(opts: {
  guidesUrl?: string;
  debug?: boolean;
}): Promise<VeoGuidesApi | null> {
  cached ??= loadInternal(opts);
  return cached;
}

function loadInternal(opts: { guidesUrl?: string; debug?: boolean }): Promise<VeoGuidesApi | null> {
  return new Promise((resolve) => {
    if (!hasWindow() || !hasDocument()) {
      resolve(null);
      return;
    }
    const win = window as unknown as WindowWithGuides;
    if (win.veoGuides) {
      resolve(win.veoGuides);
      return;
    }

    const url = opts.guidesUrl ?? deriveGuidesUrl();
    if (!url) {
      if (opts.debug) console.warn('[veo] no pude resolver la URL de veo-guides.js');
      resolve(null);
      return;
    }

    const done = (): void => resolve(win.veoGuides ?? null);
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${url}"]`);
    if (existing) {
      existing.addEventListener('load', done);
      existing.addEventListener('error', () => resolve(null));
      return;
    }

    const script = document.createElement('script');
    script.src = url;
    script.async = true;
    script.addEventListener('load', done);
    script.addEventListener('error', () => {
      if (opts.debug) console.error('[veo] no pude cargar', url);
      resolve(null);
    });
    document.head.appendChild(script);
  });
}

/** Deriva `veo-guides.js` del `<script>` que cargó `veo.js`. */
function deriveGuidesUrl(): string | null {
  const scripts = document.querySelectorAll<HTMLScriptElement>('script[src]');
  for (const script of Array.from(scripts)) {
    if (/\bveo(\.min)?\.js(\?.*)?$/.test(script.src)) {
      return script.src.replace(/\bveo(\.min)?\.js/, 'veo-guides$1.js');
    }
  }
  return null;
}
